/**
 * Route search params for the overview page.
 *
 * The window is a **preset token** plus, for a custom range only, the two
 * calendar dates that pin it. Resolved timestamps are never written for a
 * preset — see `window.ts` for why.
 *
 * `from`/`to` are validated only as far as their shape here; whether they form
 * a usable window is `resolveWindow`'s call, because a range that is inverted
 * or too wide has to reach the filter bar as an inline message rather than be
 * stripped from the URL behind the user's back.
 *
 * `preset` is optional and carries **no** parse-time default, which is what
 * lets the route tell a bare `/air/overview` apart from an explicit
 * `?preset=7d` and restore the viewer's remembered preset only for the former.
 * `resolveWindow` applies the fallback instead — see `window-preference.ts`.
 */
import { z } from 'zod'

import { OVERVIEW_PRESETS } from './window'

/** `yyyy-MM-dd` — a calendar date, deliberately not an instant. */
const calendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

export const overviewSearchSchema = z.object({
	preset: z.enum([...OVERVIEW_PRESETS, 'custom']).optional(),
	from: calendarDate.optional(),
	to: calendarDate.optional(),
})

export type OverviewSearch = z.infer<typeof overviewSearchSchema>
